/**
 * @file ScoringParsePane.tsx
 * @description
 * Document screening without AI. Workflow:
 * 1. Queue PDF / TXT documents (or sample scenarios from `SCREENING_TEMPLATES`).
 * 2. Extract text: PDF text layer, Tesseract OCR fallback for scans, plain text directly.
 * 3. Score with the deterministic rule engine (`utils/scoring`) — markers + numeric rules.
 * 4. Review the detail (scores, notes, signals that fired), export JSON or save to the CRM.
 */

import React, { useRef, useState } from "react";
import {
    Download, FileSearch, TrendingUp, ShieldAlert, Activity, Target, Settings, Zap, X,
    FlaskConical, UserPlus, AlertTriangle, Upload, Play,
} from "lucide-react";

import { MainLayout, AppButton, MessageModal, MessageState } from "../ui/components";
import { TablePane } from "../ui/TablePane";
import CrmContactDetail from "./CrmContactDetail";
import { readDocumentText } from "../../utils/PDFTextExtractor";
import {
    screenDocument,
    ScreeningError,
    ScreeningResult,
    SCREENING_TEMPLATES,
    templateToFile,
    toCrmImport,
    fromCrmImport,
    CATEGORY_LABELS,
    Category,
    ENGINE_VERSION,
} from "../../utils/scoring";
import { createEntry } from "../../api/strapi";
import { ContactFields, UserProfile } from "../../types/objects";

type FileStatus = "idle" | "processing" | "done" | "error";

export interface ScoringFile {
    id: string;
    file: File;
    status: FileStatus;
    stage?: "text" | "ocr";
    resultJson: ScreeningResult | null;
    score?: number;
    errorDetail?: string;
}

const ACCEPTED_TYPES = ".pdf,.txt,application/pdf,text/plain";

const CATEGORY_ICONS: Record<Category, React.ReactNode> = {
    vision: <Target className="text-purple-500" />,
    finance: <TrendingUp className="text-green-500" />,
    implementation: <Zap className="text-orange-500" />,
    operations: <Settings className="text-blue-500" />,
    risks: <ShieldAlert className="text-red-500" />,
};

const DETAIL_ORDER: Category[] = ["vision", "finance", "implementation", "operations", "risks"];

let idCounter = 0;
const nextId = () => `f${Date.now().toString(36)}${(idCounter++).toString(36)}`;

const toQueueItem = (file: File): ScoringFile => ({ id: nextId(), file, status: "idle", resultJson: null });

/** Downloads the CRM exchange JSON (same format the contact form imports). */
export function downloadResultJson(result: ScreeningResult, fileName: string) {
    const blob = new Blob([JSON.stringify(toCrmImport(result), null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${fileName.replace(/\.[a-z0-9]+$/i, "")}_screening.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}

export default function ScoringParsePane({ user: _user }: { user: UserProfile }) {
    const [files, setFiles] = useState<ScoringFile[]>([]);
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const [currentId, setCurrentId] = useState<string | null>(null);
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [showTemplates, setShowTemplates] = useState(false);
    const [crmDraft, setCrmDraft] = useState<Partial<ContactFields> | null>(null);
    const [message, setMessage] = useState<MessageState | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const patchFile = (id: string, patch: Partial<ScoringFile>) =>
        setFiles((prev) => prev.map((f) => (f.id === id ? { ...f, ...patch } : f)));

    const addFiles = (list: File[]) => setFiles((prev) => [...prev, ...list.map(toQueueItem)]);

    const handleFileSelection = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) addFiles(Array.from(e.target.files));
        e.target.value = "";
    };

    const addTemplates = (ids: string[]) => {
        addFiles(SCREENING_TEMPLATES.filter((t) => ids.includes(t.id)).map(templateToFile));
        setShowTemplates(false);
    };

    const processBatch = async () => {
        const queue = files.filter((f) => f.status === "idle" || f.status === "error");
        if (queue.length === 0) return;
        setIsAnalyzing(true);

        for (const item of queue) {
            setCurrentId(item.id);
            patchFile(item.id, { status: "processing", errorDetail: undefined, stage: "text" });
            try {
                const text = await readDocumentText(item.file, (stage) => patchFile(item.id, { stage }));
                const result = screenDocument(text, item.file.name);
                patchFile(item.id, { status: "done", resultJson: result, score: result.score });
            } catch (err: any) {
                console.error(`[Screening] Failed to process ${item.file.name}`, err);
                const detail = err instanceof ScreeningError
                    ? err.message
                    : "Dokument se nepodařilo přečíst (poškozené PDF nebo nečitelný sken).";
                patchFile(item.id, { status: "error", errorDetail: detail });
            }
        }

        setIsAnalyzing(false);
        setCurrentId(null);
    };

    const saveToCrm = async (payload: ContactFields) => {
        await createEntry("contacts", { ...payload });
        setMessage({ type: "success", title: "Uloženo do CRM", text: `Subjekt „${payload.fullname}“ byl založen v modulu Kontakty.` });
    };

    const selected = files.find((f) => f.id === selectedId && f.status === "done") ?? null;
    const result = selected?.resultJson ?? null;
    const pendingCount = files.filter((f) => f.status === "idle" || f.status === "error").length;
    const currentIndex = currentId ? files.findIndex((f) => f.id === currentId) : null;

    return (
        <MainLayout
            title="Screening dokumentů"
            subtitle={`Pravidlové hodnocení výročních zpráv a projektových podkladů (${ENGINE_VERSION}). Výsledek je vždy stejný pro stejný dokument – bez AI a bez odesílání dat mimo prohlížeč.`}
            actions={
                <>
                    <input type="file" ref={fileInputRef} multiple accept={ACCEPTED_TYPES} className="hidden" onChange={handleFileSelection} data-testid="screening-input" />
                    <AppButton variant="outline" onClick={() => setShowTemplates((v) => !v)}>
                        <FlaskConical size={18} /> Vzorové scénáře
                    </AppButton>
                    <AppButton variant="primary" onClick={() => fileInputRef.current?.click()}>
                        <Upload size={18} /> Nahrát PDF / TXT
                    </AppButton>
                    <AppButton variant="success" onClick={processBatch} disabled={isAnalyzing || pendingCount === 0} className="px-8 font-black">
                        <Play size={18} /> {isAnalyzing ? "Probíhá screening…" : `Spustit screening${pendingCount ? ` (${pendingCount})` : ""}`}
                    </AppButton>
                </>
            }
        >
            <div className="space-y-6 w-full text-left">
                {showTemplates && (
                    <section className="bg-white p-6 rounded-3xl border border-blue-100 shadow-sm space-y-4" aria-label="Vzorové scénáře">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                            <div>
                                <h3 className="text-lg font-black text-slate-900">Vzorové scénáře pro testování</h3>
                                <p className="text-sm text-slate-500">Fiktivní dokumenty s očekávaným výsledkem. Přidají se do fronty jako textové soubory.</p>
                            </div>
                            <AppButton variant="outline" onClick={() => addTemplates(SCREENING_TEMPLATES.map((t) => t.id))} className="text-sm">
                                Přidat všechny ({SCREENING_TEMPLATES.length})
                            </AppButton>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                            {SCREENING_TEMPLATES.map((t) => (
                                <button
                                    key={t.id}
                                    type="button"
                                    onClick={() => addTemplates([t.id])}
                                    className="text-left p-4 rounded-2xl border border-slate-200 hover:border-blue-400 hover:bg-blue-50/40 transition-all"
                                >
                                    <div className="font-black text-slate-900">{t.title}</div>
                                    <div className="text-xs text-slate-500 mt-1 leading-relaxed">{t.description}</div>
                                    <div className="text-[10px] font-black text-blue-600 uppercase tracking-widest mt-2">
                                        {t.expected.error ? "očekávaná chyba" : `očekávané skóre ${t.expected.scoreMin}–${t.expected.scoreMax} %`}
                                    </div>
                                </button>
                            ))}
                        </div>
                    </section>
                )}

                <TablePane
                    title="Fronta dokumentů"
                    files={files}
                    isAnalyzing={isAnalyzing}
                    currentIndex={currentIndex !== null && currentIndex >= 0 ? currentIndex : null}
                    onRemoveFile={(idx: number) => setFiles((prev) => prev.filter((_, i) => i !== idx))}
                    onRemoveAll={() => setFiles([])}
                    onDetailClick={(item) => setSelectedId((item as ScoringFile).id)}
                />

                {selected && result && (
                    <div className="fixed inset-0 bg-slate-950/90 backdrop-blur-sm z-[5000] flex items-center justify-center p-2 md:p-4" onClick={() => setSelectedId(null)} role="dialog" aria-modal="true" aria-label="Detail screeningu">
                        <div className="bg-white rounded-[2rem] md:rounded-[2.5rem] w-full max-w-5xl shadow-2xl overflow-hidden border-4 md:border-8 border-slate-900 flex flex-col max-h-[95vh]" onClick={(e) => e.stopPropagation()}>

                            <div className="p-6 md:p-8 bg-slate-900 text-white flex justify-between items-center shrink-0">
                                <div className="flex items-center gap-4 text-left min-w-0">
                                    <div className="p-3 bg-yellow-400 rounded-2xl text-slate-900 shadow-lg hidden md:block"><FileSearch size={28} /></div>
                                    <div className="min-w-0">
                                        <h2 className="text-xl md:text-2xl font-black text-yellow-400 leading-none uppercase">Detail screeningu</h2>
                                        <p className="text-slate-400 font-bold text-[11px] md:text-xs mt-1 uppercase tracking-widest truncate">{selected.file.name}</p>
                                    </div>
                                </div>
                                <button type="button" onClick={() => setSelectedId(null)} aria-label="Zavřít" className="p-2 hover:bg-white/10 rounded-full transition-all text-white"><X size={24} /></button>
                            </div>

                            <div className="p-5 md:p-10 space-y-8 overflow-y-auto flex-1 bg-slate-50/50 custom-scrollbar text-left">
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
                                    <div className="p-8 bg-slate-900 rounded-[2rem] flex flex-col items-center justify-center text-center shadow-xl">
                                        <span className="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-2">Celkové skóre</span>
                                        <span className="text-6xl md:text-7xl font-black text-white leading-none">{result.score}%</span>
                                    </div>
                                    <div className="p-6 bg-white border border-slate-200 rounded-[2rem] shadow-sm flex flex-col justify-center gap-1">
                                        <span className="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-1 flex items-center gap-2"><Activity size={12} /> Subjekt ({result.kind})</span>
                                        <p className="text-lg md:text-xl font-black text-slate-900 leading-tight">{result.fullname}</p>
                                        <p className="text-sm text-slate-500 font-semibold">{[result.email, result.phone, result.ico && `IČO ${result.ico}`].filter(Boolean).join(" · ") || "Kontaktní údaje nenalezeny"}</p>
                                    </div>
                                    <div className="p-6 bg-white border border-slate-200 rounded-[2rem] shadow-sm flex flex-col justify-center">
                                        <span className="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-1 flex items-center gap-2"><TrendingUp size={12} /> Klíčová finanční hodnota</span>
                                        <p className="text-lg md:text-xl font-black text-blue-600 leading-tight">{result.price}</p>
                                    </div>
                                </div>

                                {result.redFlags.length > 0 && (
                                    <div className="flex items-start gap-3 bg-red-50 border-2 border-red-200 text-red-700 p-5 rounded-[1.5rem] font-bold" role="alert">
                                        <AlertTriangle size={22} className="shrink-0" />
                                        <div>Závažná rizika: {result.redFlags.join(", ")}.</div>
                                    </div>
                                )}

                                <div className="space-y-4">
                                    <h3 className="text-xs font-black text-slate-400 uppercase tracking-[0.2em] px-2">Hodnocení podle oblastí</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {DETAIL_ORDER.map((key) => (
                                            <div key={key} className={`${key === "risks" ? "md:col-span-2" : ""} bg-white p-6 rounded-[1.5rem] border border-slate-200 shadow-sm flex flex-col gap-3`}>
                                                <div className="flex justify-between items-center">
                                                    <div className="flex items-center gap-2 font-black text-slate-800 uppercase text-xs">
                                                        {CATEGORY_ICONS[key]} {CATEGORY_LABELS[key]}
                                                        {!result.coverage[key] && <span className="ml-2 text-[10px] text-orange-500 normal-case">(bez podkladů)</span>}
                                                    </div>
                                                    <span className="px-3 py-1 bg-slate-900 text-white rounded-full font-black text-[11px]">{result.scores[key]} %</span>
                                                </div>
                                                <p className="text-sm text-slate-600 leading-relaxed bg-slate-50 p-4 rounded-xl border border-slate-100">{result.notes[key]}</p>
                                                {result.evidence[key].length > 0 && (
                                                    <div className="flex flex-wrap gap-1.5">
                                                        {result.evidence[key].map((e) => (
                                                            <span key={e.label} className={`px-2 py-0.5 rounded-full text-[11px] font-bold border ${e.weight >= 0 ? "bg-green-50 text-green-700 border-green-100" : "bg-red-50 text-red-700 border-red-100"}`}>
                                                                {e.weight >= 0 ? "+" : "−"}{Math.abs(e.weight)} {e.label}
                                                            </span>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className="bg-blue-50/50 p-8 rounded-[2rem] border-2 border-blue-100 shadow-sm space-y-4">
                                    <h4 className="text-[11px] font-black text-blue-500 uppercase tracking-widest flex items-center gap-2"><Activity size={14} /> Shrnutí a doporučení</h4>
                                    <p className="text-lg font-medium leading-relaxed text-slate-800 border-l-4 border-blue-500 pl-6">{result.analysis_finance}</p>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 pb-4">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setCrmDraft(fromCrmImport(toCrmImport(result)) as Partial<ContactFields>);
                                            setSelectedId(null);
                                        }}
                                        className="w-full flex items-center justify-center gap-3 py-6 bg-green-600 hover:bg-green-700 text-white rounded-[2rem] font-black text-lg shadow-xl transition-all active:scale-[0.98] uppercase tracking-widest"
                                    >
                                        <UserPlus size={26} /> Uložit do CRM
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => downloadResultJson(result, selected.file.name)}
                                        className="w-full flex items-center justify-center gap-3 py-6 bg-blue-600 hover:bg-blue-700 text-white rounded-[2rem] font-black text-lg shadow-xl transition-all active:scale-[0.98] uppercase tracking-widest"
                                    >
                                        <Download size={26} /> Stáhnout JSON
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <CrmContactDetail
                isOpen={!!crmDraft}
                initialData={crmDraft as any}
                onClose={() => setCrmDraft(null)}
                onSave={(payload) => saveToCrm(payload)}
            />
            <MessageModal message={message} onClose={() => setMessage(null)} />
        </MainLayout>
    );
}
